/**
 * Pure helper turning the raw event array into UI-ready rows.
 *
 * Responsibilities:
 *   - Sort by recordedAt ascending (server already does this, but the API
 *     contract doesn't mandate it; defending in depth on the client is
 *     cheap and keeps the timeline render pure of incidental ordering).
 *   - Map eventType → human-readable label.
 *   - Compute a relative "time-ago" string for the row.
 *   - Drop runs of consecutive duplicate event types so the timeline
 *     doesn't render "DEPART → DEPART → DEPART" for chatty hubs.
 *
 * Time-ago is computed against an injected `now` so tests can be deterministic.
 */
import type { PublicTrackingEventView } from './PublicMailItemView';

export interface TimelineRow {
  eventType: string;
  label: string;
  recordedAt: string;
  timeAgo: string;
  /** Outlet / hub code, or `null` if the event was recorded without one. */
  locationCode: string | null;
}

const EVENT_LABELS: Record<string, string> = {
  REGISTERED: 'Registered',
  ACCEPTED: 'Accepted at outlet',
  ACCEPT: 'Accepted at outlet',
  DEPART: 'Departed hub',
  IN_TRANSIT: 'In transit',
  OUT_FOR_DELIVERY: 'Out for delivery',
  DELIVERED: 'Delivered',
  RETURNED: 'Returned to sender',
  MARK_LOST: 'Marked lost',
  LOST: 'Lost',
  DESTROY: 'Destroyed',
  DESTROYED: 'Destroyed',
};

function labelForEvent(eventType: string): string {
  return EVENT_LABELS[eventType] ?? eventType;
}

/** Format an ISO timestamp as a human-readable "time ago" relative to `now`. */
export function formatTimeAgo(iso: string, now: Date = new Date()): string {
  const then = new Date(iso);
  const ms = now.getTime() - then.getTime();
  if (Number.isNaN(ms)) return '';
  if (ms < 0) return 'just now';
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} minute${min === 1 ? '' : 's'} ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hour${hr === 1 ? '' : 's'} ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day} day${day === 1 ? '' : 's'} ago`;
  const month = Math.floor(day / 30);
  if (month < 12) return `${month} month${month === 1 ? '' : 's'} ago`;
  const year = Math.floor(day / 365);
  return `${year} year${year === 1 ? '' : 's'} ago`;
}

export function formatEventTimeline(
  events: readonly PublicTrackingEventView[],
  now: Date = new Date(),
): TimelineRow[] {
  // 1. Stable sort by recordedAt ascending. Use a millisecond comparator so
  //    bad timestamps (NaN) drop to the end without throwing.
  const sorted = [...events].sort((a, b) => {
    const ta = Date.parse(a.recordedAt);
    const tb = Date.parse(b.recordedAt);
    if (Number.isNaN(ta) && Number.isNaN(tb)) return 0;
    if (Number.isNaN(ta)) return 1;
    if (Number.isNaN(tb)) return -1;
    return ta - tb;
  });

  // 2. Dedup CONSECUTIVE same-type events — repeated DEPART events from one
  //    hub collapse to a single row, but the same eventType reappearing
  //    later in the timeline (e.g. OUT_FOR_DELIVERY twice for two attempts)
  //    is preserved.
  const dedup: PublicTrackingEventView[] = [];
  let lastType: string | null = null;
  for (const e of sorted) {
    if (e.eventType === lastType) continue;
    dedup.push(e);
    lastType = e.eventType;
  }

  // 3. Project into TimelineRow.
  return dedup.map((e) => ({
    eventType: e.eventType,
    label: labelForEvent(e.eventType),
    recordedAt: e.recordedAt,
    timeAgo: formatTimeAgo(e.recordedAt, now),
    locationCode: e.locationCode,
  }));
}
